import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Trash2, Music, MessageSquare, UtensilsCrossed, Users, MapPin, Flame } from 'lucide-react';
import axios from 'axios';
import { popularSongs, inspirationalQuotes, mockLocations, attendanceLevels } from '../mock';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateFuneral = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [funeralName, setFuneralName] = useState('');
  const [songList, setSongList] = useState([{ title: '', artist: '' }]);
  const [quotes, setQuotes] = useState(['']);
  const [heinekenCount, setHeinekenCount] = useState(100);
  const [meadCount, setMeadCount] = useState(50);
  const [roastedMeat, setRoastedMeat] = useState(true);
  const [vikingFeast, setVikingFeast] = useState(true);
  const [additionalCatering, setAdditionalCatering] = useState('');
  const [vesselType, setVesselType] = useState('Modern Viking');
  const [attendanceLevel, setAttendanceLevel] = useState('Tribe');
  const [selectedRiver, setSelectedRiver] = useState('');
  const [selectedWaterfall, setSelectedWaterfall] = useState('');

  const addSong = () => {
    setSongList([...songList, { title: '', artist: '' }]);
  };

  const removeSong = (index) => {
    setSongList(songList.filter((_, i) => i !== index));
  };

  const updateSong = (index, field, value) => {
    const updated = [...songList];
    updated[index][field] = value;
    setSongList(updated);
  };

  const addQuote = () => {
    setQuotes([...quotes, '']);
  };

  const removeQuote = (index) => {
    setQuotes(quotes.filter((_, i) => i !== index));
  };

  const updateQuote = (index, value) => {
    const updated = [...quotes];
    updated[index] = value;
    setQuotes(updated);
  };

  const addPopularSong = (song) => {
    setSongList([...songList, { title: song.title, artist: song.artist }]);
    toast({
      title: 'Song Added',
      description: `${song.title} by ${song.artist} added to your playlist`,
    });
  };

  const addInspirationalQuote = (quote) => {
    setQuotes([...quotes, quote]);
    toast({
      title: 'Quote Added',
      description: 'Quote added to your collection',
    });
  };

  const [submitting, setSubmitting] = useState(false);
  const { token } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!funeralName.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a name for your funeral',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: funeralName,
        song_list: songList.filter(s => s.title && s.artist),
        quotes: quotes.filter(q => q.trim()),
        catering: {
          heineken: heinekenCount,
          mead: meadCount,
          roasted_meat: roastedMeat,
          viking_feast: vikingFeast,
          additional_items: additionalCatering
        },
        vessel_type: vesselType,
        attendance_level: attendanceLevel,
        location: {
          river: selectedRiver,
          waterfall: selectedWaterfall
        }
      };

      await axios.post(`${API}/funerals`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Success!',
        description: 'Your Viking funeral has been created',
      });

      navigate('/my-funerals');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create funeral',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Create Your Viking Funeral
          </h1>
          <p className="text-xl text-slate-600">
            Design a legendary sendoff that celebrates your life with fire and glory
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">Funeral Name</CardTitle>
              <CardDescription>Give your legendary sendoff a memorable name</CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                placeholder="e.g., Eric the Bold's Sendoff, Freya's Fire Festival"
                value={funeralName}
                onChange={(e) => setFuneralName(e.target.value)}
                className="text-lg"
              />
            </CardContent>
          </Card>

          <Tabs defaultValue="music" className="mb-6">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="music"><Music className="w-4 h-4 mr-2" />Music</TabsTrigger>
              <TabsTrigger value="quotes"><MessageSquare className="w-4 h-4 mr-2" />Quotes</TabsTrigger>
              <TabsTrigger value="catering"><UtensilsCrossed className="w-4 h-4 mr-2" />Catering</TabsTrigger>
              <TabsTrigger value="vessel"><Flame className="w-4 h-4 mr-2" />Vessel</TabsTrigger>
              <TabsTrigger value="attendance"><Users className="w-4 h-4 mr-2" />Attendance</TabsTrigger>
              <TabsTrigger value="location"><MapPin className="w-4 h-4 mr-2" />Location</TabsTrigger>
            </TabsList>

            <TabsContent value="music">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Epic Soundtrack</CardTitle>
                  <CardDescription>Choose the songs that will echo through eternity</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {songList.map((song, index) => (
                    <div key={index} className="flex gap-4 items-end">
                      <div className="flex-1">
                        <Label>Song Title</Label>
                        <Input
                          placeholder="Enter song title"
                          value={song.title}
                          onChange={(e) => updateSong(index, 'title', e.target.value)}
                        />
                      </div>
                      <div className="flex-1">
                        <Label>Artist</Label>
                        <Input
                          placeholder="Enter artist name"
                          value={song.artist}
                          onChange={(e) => updateSong(index, 'artist', e.target.value)}
                        />
                      </div>
                      {songList.length > 1 && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => removeSong(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" onClick={addSong} variant="outline" className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Song
                  </Button>

                  <div className="pt-6 border-t">
                    <h4 className="font-semibold text-lg mb-4">Popular Choices</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {popularSongs.map((song, idx) => (
                        <Button
                          key={idx}
                          type="button"
                          variant="outline"
                          className="justify-start"
                          onClick={() => addPopularSong(song)}
                        >
                          <Music className="w-4 h-4 mr-2" />
                          {song.title} - {song.artist}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="quotes">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Quotes from Beyond</CardTitle>
                  <CardDescription>Share your wisdom and final words with the world</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {quotes.map((quote, index) => (
                    <div key={index} className="flex gap-4 items-end">
                      <div className="flex-1">
                        <Label>Quote {index + 1}</Label>
                        <Textarea
                          placeholder="Enter your quote or wisdom"
                          value={quote}
                          onChange={(e) => updateQuote(index, e.target.value)}
                          rows={3}
                        />
                      </div>
                      {quotes.length > 1 && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => removeQuote(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" onClick={addQuote} variant="outline" className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Quote
                  </Button>

                  <div className="pt-6 border-t">
                    <h4 className="font-semibold text-lg mb-4">Inspirational Quotes</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {inspirationalQuotes.map((quote, idx) => (
                        <Button
                          key={idx}
                          type="button"
                          variant="outline"
                          className="justify-start text-left h-auto py-3"
                          onClick={() => addInspirationalQuote(quote)}
                        >
                          <MessageSquare className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span className="text-sm">{quote}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="catering">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Viking Feast</CardTitle>
                  <CardDescription>Plan the celebration feast - Heineken is mandatory!</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="heineken" className="text-lg">Heineken Beer (bottles)</Label>
                    <Input
                      id="heineken"
                      type="number"
                      min="50"
                      value={heinekenCount}
                      onChange={(e) => setHeinekenCount(parseInt(e.target.value))}
                      className="mt-2"
                    />
                    <p className="text-sm text-slate-500 mt-1">Minimum 50 bottles required</p>
                  </div>

                  <div>
                    <Label htmlFor="mead" className="text-lg">Mead (bottles)</Label>
                    <Input
                      id="mead"
                      type="number"
                      min="0"
                      value={meadCount}
                      onChange={(e) => setMeadCount(parseInt(e.target.value))}
                      className="mt-2"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="roastedMeat"
                        checked={roastedMeat}
                        onChange={(e) => setRoastedMeat(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <Label htmlFor="roastedMeat" className="text-lg cursor-pointer">
                        Roasted Meat Platter
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="vikingFeast"
                        checked={vikingFeast}
                        onChange={(e) => setVikingFeast(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <Label htmlFor="vikingFeast" className="text-lg cursor-pointer">
                        Full Viking Feast (mead, honey cakes, smoked salmon)
                      </Label>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="additional" className="text-lg">Additional Catering Items</Label>
                    <Textarea
                      id="additional"
                      placeholder="Specify any additional food or drink requirements"
                      value={additionalCatering}
                      onChange={(e) => setAdditionalCatering(e.target.value)}
                      rows={4}
                      className="mt-2"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="vessel">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Choose Your Vessel</CardTitle>
                  <CardDescription>Select the type of vessel for your legendary sendoff</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div
                      onClick={() => setVesselType('Modern Viking')}
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all duration-300 ${
                        vesselType === 'Modern Viking'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-5xl mb-4">🛥️</div>
                        <div className="text-2xl font-bold text-slate-900 mb-2">Modern Viking</div>
                        <p className="text-sm text-slate-600 mb-3">
                          Sea Ray or luxury boat - contemporary style meets Viking tradition
                        </p>
                        <div className="text-lg font-bold text-blue-600">Premium Option</div>
                      </div>
                    </div>

                    <div
                      onClick={() => setVesselType('Original Viking')}
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all duration-300 ${
                        vesselType === 'Original Viking'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-5xl mb-4">⛵</div>
                        <div className="text-2xl font-bold text-slate-900 mb-2">Original Viking</div>
                        <p className="text-sm text-slate-600 mb-3">
                          Wooden schooner - authentic Norse vessel for a true Viking experience
                        </p>
                        <div className="text-lg font-bold text-amber-600">Most Authentic</div>
                      </div>
                    </div>

                    <div
                      onClick={() => setVesselType('Wanna Be a Viking')}
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all duration-300 ${
                        vesselType === 'Wanna Be a Viking'
                          ? 'border-slate-500 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-5xl mb-4">⚰️</div>
                        <div className="text-2xl font-bold text-slate-900 mb-2">Wanna Be a Viking</div>
                        <p className="text-sm text-slate-600 mb-3">
                          Standard wooden coffin - budget-friendly option with Viking spirit
                        </p>
                        <div className="text-lg font-bold text-slate-600">Value Choice</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="attendance">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Choose Your Attendance Level</CardTitle>
                  <CardDescription>Select the scale of your legendary sendoff</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    {attendanceLevels.map((level) => (
                      <div
                        key={level.name}
                        onClick={() => setAttendanceLevel(level.name)}
                        className={`p-6 border-2 rounded-lg cursor-pointer transition-all duration-300 ${
                          attendanceLevel === level.name
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-3xl font-bold text-slate-900 mb-2">{level.name}</div>
                          <div className="text-slate-600 mb-4">{level.capacity}</div>
                          <p className="text-sm text-slate-600 mb-4">{level.description}</p>
                          <div className="text-2xl font-bold text-amber-600">{level.price}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="location">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Sacred Locations</CardTitle>
                  <CardDescription>Find the perfect river or waterfall for your pyre</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="river" className="text-lg">Choose a River</Label>
                    <Select value={selectedRiver} onValueChange={setSelectedRiver}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select a river" />
                      </SelectTrigger>
                      <SelectContent>
                        {mockLocations
                          .filter(loc => loc.type === 'river')
                          .map((loc) => (
                            <SelectItem key={loc.name} value={loc.name}>
                              {loc.name}, {loc.state}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="waterfall" className="text-lg">Choose a Waterfall</Label>
                    <Select value={selectedWaterfall} onValueChange={setSelectedWaterfall}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select a waterfall" />
                      </SelectTrigger>
                      <SelectContent>
                        {mockLocations
                          .filter(loc => loc.type === 'waterfall')
                          .map((loc) => (
                            <SelectItem key={loc.name} value={loc.name}>
                              {loc.name}, {loc.state}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(selectedRiver || selectedWaterfall) && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold mb-3">Selected Locations:</h4>
                      {selectedRiver && (
                        <div className="mb-2">
                          <span className="font-medium">River:</span> {selectedRiver}
                          <a
                            href={mockLocations.find(l => l.name === selectedRiver)?.mapLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-3 text-blue-600 hover:underline"
                          >
                            View on Map
                          </a>
                        </div>
                      )}
                      {selectedWaterfall && (
                        <div>
                          <span className="font-medium">Waterfall:</span> {selectedWaterfall}
                          <a
                            href={mockLocations.find(l => l.name === selectedWaterfall)?.mapLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-3 text-blue-600 hover:underline"
                          >
                            View on Map
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/')}>
              Cancel
            </Button>
            <Button type="submit" size="lg" className="bg-amber-600 hover:bg-amber-700" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Funeral'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFuneral;